import { Box, Divider, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQuery } from '@tanstack/react-query';
import { MedicalNoteType, getMedicalNoteReview } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useUserQuery } from '@uplift/queries';
import { Alert, DischargeNoteReview as DischargeNoteReviewComponent } from '@uplift/ui-v2';

export type DischargeNoteReviewProps = {
  patientId: string;
  noteId: string;
};

export const DischargeNoteReview: React.VFC<DischargeNoteReviewProps> = props => {
  const { classes } = useDischargeNoteReviewStyles();
  const me = useCurrentuser();
  const { data: patient, isInitialLoading: isPatientLoading } = useUserQuery(props.patientId);
  const { data: dischargeNoteReview, isInitialLoading: isReviewLoading } = useQuery({
    queryKey: [ 'medical-note-review' ],
    queryFn: async () => {
      const response = await getMedicalNoteReview<MedicalNoteType.therapyDischargeNote | MedicalNoteType.psychiatryDischargeNote>(props.noteId);

      return response.data;
    },
    cacheTime: 0,
  });

  if (isPatientLoading || isReviewLoading) {
    return null;
  }

  const title = `${patient.firstName} ${patient.lastName}`;
  const subtitle = `with ${me.firstName[0]}. ${me.lastName}`;

  return (
    <div>
      <Box mb={2}>
        <Alert title="Review your discharge note before you submit" severity="info">
          <Typography variant="body2" color="textSecondary">
            You won't be able to make changes once you submit this note. Other providers can access it as part of this client's file.
          </Typography>
        </Alert>
      </Box>
      <Paper variant="outlined">
        <Box px={10} pt={5} pb={10} display="flex" flexDirection="column">
          <Box className={classes.title}>
            <Typography>{title}</Typography>
            <Typography>{subtitle}</Typography>
          </Box>
          <Box pt={2}>
            <Divider />
          </Box>
          <div>
            <DischargeNoteReviewComponent provider={me} note={dischargeNoteReview} />
          </div>
        </Box>
      </Paper>
    </div>
  );
};

const useDischargeNoteReviewStyles = makeStyles({ name: 'DischargeNoteReview' })({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 500,
  },
});
